import React, { useContext, useEffect } from 'react';
import Page from '../..';
import Table, { TableHeader } from '../../../Table';
import { setFilterColumn } from '../../../../store/utils';
import SVG from '../../../SVG';
import Form from '../../../Form';
import { ExportContext } from '../../../../provider/ExportProvider';
import { formatBRL } from '../../../../store/masks';
import { useAbility } from '../../../Hooks/useAbility';

const PageExportsDeposits = () => {
  const {
    deposits,
    checkDepositIds,
    allDepositChecks,
    depositCheckAll,
    checkDepositId,
    deleteDepositLoadings,
    editDeposit,
    searchFilter,
    setSearchFilter,
    getFilterField,
    getSortField,
    setSortField,
    getAutocompleteItems,
    setAutocompleteItems,
    setField,
    onLineClickHandler,
    setInvoice,
    TableControl,
    DailyTableControl,
    reportDeposit,
    dailyDeposits,
    reportDailyDeposit,
    getSortDailyField,
    setSortDailyField,
    setModal,
    filterGroupDeposit,
    user,
    selectedGroupId,
  } = useContext(ExportContext)!.controllers.deposit;
  const { Can, ability } = useAbility();
  const canDeleteDeposit = ability?.can('delete', 'Deposit');
  const canUpdateDeposit = ability?.can('update', 'Deposit');
  const canUpdateOrDeleteDeposit = canDeleteDeposit || canUpdateDeposit;
  const canUpdateAndDeleteDeposit = canDeleteDeposit && canUpdateDeposit;

  const items = deposits.map((deposit, index) => ({
    $isSelected: checkDepositIds.includes(deposit.id),
    check: () => (
      <Form.Input.Checkbox
        checked={checkDepositIds.includes(deposit.id)}
        onChange={() => checkDepositId(deposit.id)}
      />
    ),
    ...deposit,
    ...(() =>
      canUpdateOrDeleteDeposit
        ? {
          control: () => (
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                columnGap: '10px',
              }}
            >
              <Can I="update" a="Deposit">
                {() => (
                  <label
                    style={{
                      display: 'flex',
                      cursor: 'pointer',
                      alignItems: 'center',
                    }}
                  >
                    {true ? (
                      <SVG.Icon.Edit
                        onClick={() => {
                          editDeposit(deposit);
                        }}
                        width={25}
                        height={25}
                      />
                    ) : (
                      <SVG.Icon.Loader width={25} height={25} />
                    )}
                  </label>
                )}
              </Can>
              <Can I="delete" a="Deposit">
                {() => (
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                    }}
                  >
                    {!deleteDepositLoadings[index] ? (
                      <SVG.Icon.Trash
                        style={{ padding: '0 2.5px' }}
                        onClick={() => {
                          setModal('single.deleteDeposits', true);
                          setInvoice('selected.deposit', deposit.id);
                        }}
                        width={20}
                        height={20}
                      />
                    ) : (
                      <SVG.Icon.Loader width={25} height={25} />
                    )}
                  </label>
                )}
              </Can>
            </label>
          ),
        }
        : {})(),
  }));

  const headers: TableHeader[] = [
    {
      text: (
        <Form.Input.Checkbox
          checked={allDepositChecks}
          onChange={depositCheckAll}
        />
      ),
      value: 'check',
      width: 'auto',
      totalMask: () => <div style={{ minWidth: '35px' }} />,
      total: 0,
    },
    {
      text: 'Valor',
      value: 'value',
      width: '0.7fr',
      mask: ({ value }) => formatBRL(value),
      sort: {
        value: getSortField('value'),
        onChange: setSortField('value'),
      },
      totalMask: formatBRL,
      total: reportDeposit.total,
      lock: 'locked',
    },
    {
      text: 'Companhia',
      value: 'company.name',
      width: '1fr',
      sort: {
        value: getSortField('company'),
        onChange: setSortField('company'),
      },
      filter: {
        value: getFilterField('company'),
        onChange: setField('company'),
        items: getAutocompleteItems('company'),
        handleItems: () => setAutocompleteItems('company'),
        label: 'Companhia',
      },
      onClear: () => {
        setSearchFilter(setFilterColumn('company', '', searchFilter));
        setInvoice(`autocompletes.deposits.company.filter.value`, '');
      },
      lock: 'locked',
    },
    {
      text: 'Data',
      value: 'date',
      width: '1fr',
      mask: ({ date }) => new Date(date).toLocaleString('pt-BR').split(',')[0],
      sort: {
        value: getSortField('date'),
        onChange: setSortField('date'),
      },
      interval: [
        {
          value: getFilterField('date', 'GEQ'),
          onChange: setField('date', 'GEQ'),
          label: 'Data inicial',
        },
        {
          value: getFilterField('date', 'LT'),
          onChange: setField('date', 'LT'),
          label: 'Data final',
        },
      ],
      tooltip: {
        translateX: 0.5,
      },
      onClear: () => {
        let filter = setFilterColumn('date', '', searchFilter, 'GEQ');
        filter = setFilterColumn('date', '', filter, 'LT');
        setSearchFilter(filter);
      },
      lock: 'locked',
    },
    {
      text: 'Referência',
      value: 'reference',
      width: '0.7fr',
      mask: ({ reference }) =>
        new Date(reference).toLocaleString('pt-BR').split(',')[0],
      color: ({ date, reference }) =>
        date !== reference ? '#ff0050' : 'black',
      sort: {
        value: getSortField('reference'),
        onChange: setSortField('reference'),
      },
      interval: [
        {
          value: getFilterField('reference', 'GEQ'),
          onChange: setField('reference', 'GEQ'),
          label: 'Data inicial',
        },
        {
          value: getFilterField('reference', 'LT'),
          onChange: setField('reference', 'LT'),
          label: 'Data final',
        },
      ],
      tooltip: {
        translateX: 0.5,
      },
      onClear: () => {
        let filter = setFilterColumn('reference', '', searchFilter, 'GEQ');
        filter = setFilterColumn('reference', '', filter, 'LT');
        setSearchFilter(filter);
      },
      lock: 'locked',
    },
    {
      text: 'Criado em',
      value: 'createdAt',
      width: '170px',
      mask: ({ createdAt }) => new Date(createdAt).toLocaleString('pt-BR'),
      sort: {
        value: getSortField('createdAt'),
        onChange: setSortField('createdAt'),
      },
      interval: [
        {
          value: getFilterField('createdAt', 'GEQ'),
          onChange: setField('createdAt', 'GEQ'),
          label: 'Data inicial',
        },
        {
          value: getFilterField('createdAt', 'LT'),
          onChange: setField('createdAt', 'LT'),
          label: 'Data final',
        },
      ],
      tooltip: {
        translateX: 0.5,
      },
      onClear: () => {
        let filter = setFilterColumn('createdAt', '', searchFilter, 'GEQ');
        filter = setFilterColumn('createdAt', '', filter, 'LT');
        setSearchFilter(filter);
      },
      lock: 'locked',
    },
    {
      text: 'Atualizado em',
      value: 'updatedAt',
      width: '170px',
      mask: ({ updatedAt }) => new Date(updatedAt).toLocaleString('pt-BR'),
      sort: {
        value: getSortField('updatedAt'),
        onChange: setSortField('updatedAt'),
      },
      tooltip: {
        translateX: 1,
      },
      interval: [
        {
          value: getFilterField('updatedAt', 'GEQ'),
          onChange: setField('updatedAt', 'GEQ'),
          label: 'Data inicial',
        },
        {
          value: getFilterField('updatedAt', 'LT'),
          onChange: setField('updatedAt', 'LT'),
          label: 'Data final',
        },
      ],
      onClear: () => {
        let filter = setFilterColumn('updatedAt', '', searchFilter, 'GEQ');
        filter = setFilterColumn('updatedAt', '', filter, 'LT');
        setSearchFilter(filter);
      },
      lock: 'locked',
    },
    ...(() =>
      canUpdateOrDeleteDeposit
        ? [
          {
            text: '',
            value: 'control',
            width: canUpdateAndDeleteDeposit ? '80px' : '40px',
            style: {
              margin: 'auto 10px auto auto',
            },
          },
        ]
        : [])(),
  ];

  const dailyHeaders = [
    {
      text: 'Valor',
      value: 'value',
      width: '1fr',
      mask: ({ value }) => formatBRL(value),
      totalMask: formatBRL,
      total: reportDailyDeposit.total,
      sort: {
        value: getSortDailyField('value'),
        onChange: setSortDailyField('value'),
      },
    },
    {
      text: 'Referência',
      value: 'reference',
      width: '1fr',
      mask: ({ reference }) =>
        new Date(reference).toLocaleString('pt-BR').split(',')[0],
      sort: {
        value: getSortDailyField('reference'),
        onChange: setSortDailyField('reference'),
      },
    },
    {
      text: 'Quantidade',
      value: 'quantity',
      width: '1fr',
      total: reportDailyDeposit.quantity,
      sort: {
        value: getSortDailyField('quantity'),
        onChange: setSortDailyField('quantity'),
      },
    },
  ];

  const dailyItems = dailyDeposits;

  useEffect(() => {
    if (!selectedGroupId)
      filterGroupDeposit();
  }, [user, selectedGroupId])

  return (
    <Page className="page-banner">
      <div
        style={{
          display: 'flex',
          width: 'calc(100% - 22px)',
          gap: '10px',
          marginLeft: '16px',
        }}
      >
        <div
          style={{
            position: 'relative',
            transition: 'min-width 500ms, max-width 500ms',
            maxWidth: 'calc(100% - 550px - 10px - 10px)',
            minWidth: 'calc(100% - 550px - 10px - 10px)',
            minHeight: 'calc(100vh - 130px)',
            maxHeight: 'calc(100vh - 130px)',
            margin: '0',
            background: '#DDDDDD',
            borderRadius: '5px',
            overflow: 'hidden',
          }}
        >
          <Table
            headers={headers}
            items={items}
            onLineClick={onLineClickHandler}
            size="small"
            containerStyle={{
              height: 'calc(100vh - 210px)',
            }}
          >
            <TableControl />
          </Table>
        </div>
        <div
          style={{
            position: 'relative',
            transition: 'min-width 500ms, max-width 500ms',
            maxWidth: '550px',
            minWidth: '550px',
            minHeight: 'calc(100vh - 130px)',
            maxHeight: 'calc(100vh - 130px)',
            margin: '0',
            background: '#DDDDDD',
            borderRadius: '5px',
            overflow: 'hidden',
          }}
        >
          <Table
            headers={dailyHeaders}
            items={dailyItems}
            size="small"
            containerStyle={{
              height: 'calc(100vh - 210px)',
            }}
          >
            <DailyTableControl />
          </Table>
        </div>
      </div>
    </Page>
  );
};

export default PageExportsDeposits;
